import { Database, Tables } from '@dolfin/business/db_types'
import { useSupabaseClient } from '@dolfin/hooks/supabase'
import { useIssueUnreadActivity } from '@dolfin/hooks/useIssueUnreadActivity'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query'
import { QueryKeys } from './queryKeys'
import { useOrgContext } from './state/organization'

export type IssueStage = Tables<'issues'>['status']
export type IssueStatusFilter = IssueStage | 'all'

const PAGE_SIZE = 50

export type ProfileRow = Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'>
export type PayoutRow = Pick<
  Tables<'payouts'>,
  | 'id'
  | 'value'
  | 'description'
  | 'effective_date'
  | 'profile_id'
  | 'organization_id'
  | 'compensation'
> & {
  deals: Pick<Tables<'deals'>, 'deal_id' | 'name'> | null
  invoices:
    | (Pick<Tables<'invoices'>, 'invoice_number'> & {
        deals: Pick<Tables<'deals'>, 'deal_id' | 'name'> | null
      })
    | null
  profiles: ProfileRow | null
  compensation_rules: Pick<Tables<'compensation_rules'>, 'label' | 'source'> | null
}
export type StatementRow = Pick<
  Tables<'statements'>,
  'id' | 'period_start' | 'period_end' | 'profile_id' | 'organization_id'
> & {
  profiles: ProfileRow | null
}

export type IssueWithJoins = Pick<
  Tables<'issues'>,
  | 'id'
  | 'created_at'
  | 'updated_at'
  | 'created_by'
  | 'resolved_at'
  | 'resolved_by'
  | 'status'
  | 'payout_id'
  | 'statement_id'
  | 'organization_id'
> & {
  profiles: ProfileRow | null
  resolved_profiles: ProfileRow | null
  payouts?: PayoutRow | null
  statements?: StatementRow | null
}

/** Single select for issues with optional payouts and statements (left joins). Used for stable pagination. */
export const ISSUES_UNIFIED_SELECT = `id, created_at, updated_at, created_by, resolved_at, resolved_by, status, payout_id, statement_id, organization_id,
  profiles!public_issues_profile_id_fkey(id, name, email, img_url),
  resolved_profiles:profiles!public_issues_resolved_by_fkey(id, name, email, img_url),
  payouts(id, value, description, effective_date, profile_id, organization_id, compensation,
    deals(deal_id, name),
    invoices(invoice_number, deals(deal_id, name)),
    profiles!payouts_profile_id_fkey(id, name, email, img_url),
    compensation_rules(label, source)),
  statements(id, period_start, period_end, profile_id, organization_id,
    profiles!statements_profile_id_fkey(id, name, email, img_url))`

export interface UseIssuesOptions {
  selectedIssueId?: number
}

interface IssuesPage {
  items: IssueWithJoins[]
  nextCursor: number | null
}

export function useIssues(options: UseIssuesOptions = {}) {
  const { selectedIssueId } = options
  const supabase = useSupabaseClient<Database>()
  const queryClient = useQueryClient()
  const organizationId = useOrgContext(state => state.selectedOrganization?.id)
  const currentUserId = useOrgContext(state => state.profile?.id)
  const {
    unreadIssueIds,
    unreadIssueCount,
    unreadCountsByStage,
    markIssueActivityAsRead,
    markIssueActivityAsUnread
  } = useIssueUnreadActivity()

  const [statusFilter, setStatusFilter] = useState<IssueStatusFilter>('open')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedId, setSelectedId] = useState<number | undefined>(selectedIssueId)

  // Sync selection from URL. When URL has no issueId, clear selection. Do not clear based on "issue not in list" while issues are loading (that would break deep links).
  useEffect(() => {
    setSelectedId(selectedIssueId ?? undefined)
  }, [selectedIssueId])

  // Single query: issues with optional payouts/statements, filtered by org and status. Each issue appears once, so page size is stable.
  const fetchIssuesPage = useCallback(
    async (pageParam: number, status: IssueStatusFilter): Promise<IssuesPage> => {
      if (!organizationId) return { items: [], nextCursor: null }

      const from = pageParam * PAGE_SIZE
      const to = from + PAGE_SIZE - 1

      let query = supabase
        .from('issues')
        .select(ISSUES_UNIFIED_SELECT)
        .eq('organization_id', organizationId!)

      if (status !== 'all') {
        query = query.eq('status', status)
      }

      const { data, error } = await query.order('updated_at', { ascending: false }).range(from, to)

      if (error) throw error

      const items = (data ?? []) as IssueWithJoins[]
      const hasMore = items.length === PAGE_SIZE
      return {
        items,
        nextCursor: hasMore ? pageParam + 1 : null
      }
    },
    [organizationId, supabase]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queryKey = [QueryKeys.issues.get, organizationId, statusFilter]

  const {
    data: infiniteData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(queryKey, ({ pageParam = 0 }) => fetchIssuesPage(pageParam, statusFilter), {
    enabled: !!organizationId,
    getNextPageParam: lastPage => lastPage.nextCursor
  })

  // Flatten all pages into a single deduplicated list, then filter/sort
  const rawIssues = useMemo(() => {
    if (!infiniteData?.pages) return []
    const byId = new Map<number, IssueWithJoins>()
    infiniteData.pages.forEach(page => {
      page.items.forEach(i => byId.set(i.id, i))
    })
    return Array.from(byId.values()).sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )
  }, [infiniteData])

  // Fetch a single issue by ID when selectedIssueId is set but not in the paginated list (e.g. direct link to an older issue).
  const fetchIssueById = useCallback(
    async (issueId: number): Promise<IssueWithJoins | null> => {
      if (!organizationId) return null
      const { data, error } = await supabase
        .from('issues')
        .select(ISSUES_UNIFIED_SELECT)
        .eq('organization_id', organizationId)
        .eq('id', issueId)
        .maybeSingle()
      if (error) throw error
      return (data ?? null) as IssueWithJoins | null
    },
    [organizationId, supabase]
  )

  const { data: selectedIssueFromQuery, isLoading: isLoadingSelectedIssue } = useQuery(
    [QueryKeys.issues.get, organizationId, 'single', selectedIssueId],
    () => fetchIssueById(selectedIssueId!),
    {
      enabled:
        !!organizationId &&
        selectedIssueId != null &&
        !rawIssues?.some(i => i.id === selectedIssueId)
    }
  )

  const issues = useMemo(() => {
    // rawIssues is already filtered by statusFilter at the DB in fetchIssuesPage
    let list = rawIssues
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      list = list.filter(issue => {
        const creator = issue.profiles?.name ?? issue.profiles?.email ?? ''
        const resolved = issue.resolved_profiles?.name ?? issue.resolved_profiles?.email ?? ''
        const payout = issue.payouts
        const statement = issue.statements
        const payoutDesc = payout?.description ?? ''
        const dealName = payout?.deals?.name ?? ''
        const invoiceDeal = payout?.invoices?.deals?.name ?? ''
        const profileName = payout?.profiles?.name ?? statement?.profiles?.name ?? ''
        const searchable = [creator, resolved, payoutDesc, dealName, invoiceDeal, profileName]
          .join(' ')
          .toLowerCase()
        return searchable.includes(q)
      })
    }
    // Sort: unread issues first, then by updated_at desc
    return [...list].sort((a, b) => {
      const aUnread = unreadIssueIds.has(a.id) ? 1 : 0
      const bUnread = unreadIssueIds.has(b.id) ? 1 : 0
      if (aUnread !== bUnread) return bUnread - aUnread
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawIssues, statusFilter, searchQuery, unreadIssueIds])

  // Derive from unfiltered rawIssues (or single-issue fetch) so deep links to
  // resolved or filtered-out issues still show the correct ticket.
  const selectedIssue = useMemo(() => {
    if (!selectedId) return undefined
    const fromRaw = rawIssues.find(i => i.id === selectedId)
    if (fromRaw) return fromRaw
    // Direct link / ID not in loaded pages: use single-issue fetch result
    if (selectedIssueFromQuery != null) return selectedIssueFromQuery
    return undefined
  }, [rawIssues, selectedId, selectedIssueFromQuery])

  const selectIssue = useCallback((issue: IssueWithJoins | undefined) => {
    setSelectedId(issue?.id)
  }, [])

  const refetch = useCallback(() => {
    queryClient.invalidateQueries(queryKey)
    queryClient.invalidateQueries([QueryKeys.issues.get, organizationId, 'single'])
    queryClient.invalidateQueries([QueryKeys.issues.get, organizationId, 'unread'])
    return queryClient.invalidateQueries([QueryKeys.issues.get, organizationId])
  }, [queryClient, queryKey, organizationId])

  const setIssueStatus = useCallback(
    async (issueId: number, status: IssueStage) => {
      if (!currentUserId || !organizationId) return
      const isResolved = status === 'resolved'
      const { error } = await supabase
        .from('issues')
        .update({
          status,
          resolved_by: isResolved ? currentUserId : null,
          resolved_at: isResolved ? new Date().toISOString() : null
        })
        .eq('id', issueId)
        .eq('organization_id', organizationId)

      if (error) throw error
      await refetch()
    },
    [currentUserId, organizationId, supabase, refetch]
  )

  useEffect(() => {
    if (!organizationId) return
    const issuesChannel = supabase
      .channel('issues-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'issues',
          filter: `organization_id=eq.${organizationId}`
        },
        () => {
          queryClient.invalidateQueries(queryKey)
          queryClient.invalidateQueries([QueryKeys.issues.get, organizationId, 'unread'])
          queryClient.invalidateQueries([QueryKeys.issues.get, organizationId])
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(issuesChannel)
    }
  }, [organizationId, queryClient, queryKey, supabase])

  return {
    issues,
    isLoading,
    isLoadingSelectedIssue: isLoadingSelectedIssue ?? false,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    selectedIssue,
    selectIssue,
    resolveIssue: (issueId: number) => setIssueStatus(issueId, 'resolved'),
    setIssueStatus,
    refetch,
    unreadIssueIds,
    unreadIssueCount,
    unreadCountsByStage,
    markIssueActivityAsRead,
    markIssueActivityAsUnread,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage
  }
}